/*
 * Suit Builder — sections/suit-builder.liquid
 *
 * Hydrates [data-suit-builder]: fetches every step's collection from
 * /collections/<handle>/products.json (all pages), ranks candidates against the
 * chosen jacket ("Made to go together" = same brand + same range/style family +
 * same colour), suggests sizes (trousers = jacket chest − 6, waistcoat = chest,
 * length follows the jacket), keeps the build in the URL (?jacket=handle.variantId…)
 * and sessionStorage, and adds the whole look with one /cart/add.js items[] call.
 * Review step: "The look" collage (arrow-flick any piece through its match-ordered
 * alternatives in the same size) + "Your looks" saved to localStorage to flick between
 * (dispatches `cart:added` so the cart drawer opens).
 *
 * Vanilla, IIFE, data-* driven, no dependencies. Event delegation on the root via
 * data-sb-action.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-suit-builder]');
  if (!root) return;
  var cfgEl = root.querySelector('[data-sb-config]');
  if (!cfgEl) return;
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var I = cfg.i18n || {};
  var STEPS = (cfg.steps || []).filter(function (s) { return s.enabled && s.handle; });
  if (!STEPS.length) return;
  // Hidden "second pair" step: not in the stepper, filled from the shoes upsell
  // (formal shoes chosen → a comfy pair is offered, and vice versa).
  var SHOES = STEPS.filter(function (s) { return s.key === 'shoes'; })[0] || null;
  var UPSELL = null;
  if (SHOES && cfg.shoesUpsell) {
    UPSELL = { key: 'shoes_alt', label: (cfg.i18n && cfg.i18n.second_pair) || 'Second pair', handle: SHOES.handle, url: SHOES.url, required: false, enabled: true, hidden: true, count: SHOES.count, hint: '' };
    STEPS.push(UPSELL);
  }
  var VISIBLE = STEPS.filter(function (s) { return !s.hidden; });
  var STORE_KEY = 'mccalls:suit-builder';
  var LOOKS_KEY = 'mccalls:suit-looks';   // saved looks (localStorage, survives the session)
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ */
  /* Utilities                                                           */
  /* ------------------------------------------------------------------ */
  var ta = document.createElement('textarea');
  function decode(s) { ta.innerHTML = s == null ? '' : String(s); return ta.value; }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function t(key, vars) {
    var s = I[key] || key;
    if (vars) {
      Object.keys(vars).forEach(function (k) {
        s = s.replace(new RegExp('\\{\\{\\s*' + k + '\\s*\\}\\}', 'g'), vars[k]);
      });
    }
    return s;
  }
  function money(n) {
    var fmt = cfg.moneyFormat || '£{{amount}}';
    var num = Number(n) || 0;
    var fixed = num.toFixed(2);
    var parts = fixed.split('.');
    var withThousands = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var out = fmt
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(num)).replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace(/\{\{\s*amount\s*\}\}/g, withThousands + '.' + parts[1]);
    // Strip any tags Shopify may include in the money format.
    return out.replace(/<[^>]+>/g, '');
  }
  function img(src, w) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + (w || 600);
  }
  function numeric(v) { var n = parseFloat(String(v).replace(/[^\d.]/g, '')); return isNaN(n) ? null : n; }
  function sortSizes(list) {
    return list.slice().sort(function (a, b) {
      var na = numeric(a), nb = numeric(b);
      if (na != null && nb != null) return na - nb;
      if (na != null) return -1;
      if (nb != null) return 1;
      return String(a).localeCompare(String(b));
    });
  }
  function fitLabel(f) {
    var k = String(f || '').trim().toUpperCase();
    if (k === 'S' || k === 'SHORT') return t('fit_short');
    if (k === 'R' || k === 'REG' || k === 'REGULAR') return t('fit_regular');
    if (k === 'L' || k === 'LONG') return t('fit_long');
    return f;
  }
  var COMFY_VENDOR = /^(ecco|tommy hilfiger|kickers|hush puppies|skechers|nike|clarks|geox|hype)$/i;
  var COMFY_TITLE = /\b(trainers?|sneakers?|runners?|boots?|chelsea|slip[- ]?ons?|casual|mesh|canvas|gore-?tex|moc-?toe|walking|comfort)\b/i;
  function shoeStyle(p) {
    if (COMFY_VENDOR.test(p.vendor) || COMFY_TITLE.test(p.title)) return 'comfy';
    return 'formal';
  }
  function fitOrder(f) { var k = String(f).toUpperCase(); return k === 'S' ? 0 : k === 'R' ? 1 : k === 'L' ? 2 : 3; }
  function push(evt, data) {
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push(Object.assign({ event: evt }, data || {})); } catch (e) { /* noop */ }
  }

  /* Colour handling ---------------------------------------------------- */
  var SWATCHES = [
    [/\b(navy|ink|midnight)\b/, '#1c2a44', 'blue'],
    [/\b(black)\b/, '#111111', 'black'],
    [/\b(charcoal|dark grey|dark gray)\b/, '#4a4d52', 'grey'],
    [/\b(silver|light grey|light gray|grey|gray|steel)\b/, '#a9adb3', 'grey'],
    [/\b(sky|light blue|pale blue)\b/, '#8fb8dd', 'blue'],
    [/\b(royal|cobalt|electric)\b/, '#2a52be', 'blue'],
    [/\b(marine|blue|teal|petrol)\b/, '#33578a', 'blue'],
    [/\b(sage|olive|forest|green|khaki|moss)\b/, '#6f8a6b', 'green'],
    [/\b(stone|beige|sand|cream|oatmeal|ecru|camel|taupe|natural)\b/, '#cbbba0', 'stone'],
    [/\b(tan|brown|chestnut|cognac|chocolate|mocha|walnut)\b/, '#8a5a3b', 'brown'],
    [/\b(blush|pink|rose|coral|salmon)\b/, '#e3b2b8', 'pink'],
    [/\b(grape|purple|plum|lilac|lavender|aubergine)\b/, '#6b4a7a', 'purple'],
    [/\b(burgundy|wine|claret|maroon|oxblood|bordeaux)\b/, '#6e2434', 'red'],
    [/\b(red|rust|orange|terracotta)\b/, '#b2412f', 'red'],
    [/\b(white|ivory|off white)\b/, '#f6f4ee', 'white'],
    [/\b(yellow|gold|mustard|lemon)\b/, '#d9b23c', 'yellow']
  ];
  function colourInfo(name) {
    var n = String(name || '').toLowerCase();
    for (var i = 0; i < SWATCHES.length; i++) {
      if (SWATCHES[i][0].test(n)) return { swatch: SWATCHES[i][1], family: SWATCHES[i][2] };
    }
    return { swatch: '#d8d8d8', family: 'other' };
  }
  function normColour(name) {
    return String(name || '').toLowerCase().replace(/\b(blue|navy)\b\s*$/, function (m) { return m; }).replace(/[^a-z ]/g, '').replace(/\s+/g, ' ').trim();
  }

  /* Range / style-family tokens ---------------------------------------- */
  var STOP = /^(MIX|MATCH|AND|SUIT|SUITS|JACKET|JKT|TROUSER|TROUSERS|TRS|WAISTCOAT|WC|SBWC|DBWC|TUX|TUXEDO|DINNER|SLIM|TAPERED|TAILORED|REGULAR|CLASSIC|FIT|LEG|BOYS|MENS|CLUB|WEDDING|CHECK|TWEED|STRIPE|PLAIN|BLACK|NAVY|BLUE|GREY|GRAY|GREEN|STONE|SAGE|TAN|SKY|MARINE|ROYAL|OLIVE|FOREST|GRAPE|BLUSH|CHARCOAL|SILVER|BEIGE|CREAM|LIGHT|DARK|INK|WITH|THE|OF|SB|DB|EFFECT|HERRINGBONE|STRETCH|WOOL|RICH|LINEN|BLEND|CONTRAST|BUTTONS|PEAK|LAPEL|DOUBLE|BREASTED|SINGLE|SCOOP|FORMAL|FLEX|MODERN|LUXURY|SOLID|MARL|CITY|BOYS)$/;
  function familyOf(p) {
    var toks = {};
    var digits = '';
    var colourCode = '';
    var style = p.styleCode || '';
    var raw = style.split(/[\s\/+\-]+/).filter(Boolean);
    raw.forEach(function (tok, idx) {
      var up = tok.toUpperCase();
      if (/^[A-Z]+$/.test(up) && up.length >= 4 && !STOP.test(up)) toks[up] = 1;
      if (idx === 0) {
        var d = up.replace(/[^\d]/g, '');
        if (d.length >= 3) digits = d.slice(-3);
      } else if (/^\d{2}$/.test(up)) {
        colourCode = up;
      }
      // Mixed tokens like "MM7096" or "J5658" — the letters are type prefixes.
      var m = up.match(/^[A-Z]{1,2}(\d{3,})[A-Z]?$/);
      if (m && idx === 0) digits = m[1].slice(-3);
    });
    // Title range word: first capitalised word that isn't a stop word.
    var words = decode(p.title).split(/[\s\-–—]+/);
    for (var i = 0; i < Math.min(words.length, 3); i++) {
      var w = words[i].replace(/[^A-Za-z]/g, '').toUpperCase();
      if (w.length >= 3 && !STOP.test(w)) { toks[w] = 1; break; }
    }
    return { tokens: Object.keys(toks), digits: digits, colourCode: colourCode };
  }
  function matchScore(anchor, cand) {
    if (!anchor) return 0;
    var score = 0;
    if (anchor.vendor === cand.vendor) score += 3; else return 0;
    var shared = cand.family.tokens.some(function (tk) { return anchor.family.tokens.indexOf(tk) > -1; });
    if (shared) score += 4;
    if (anchor.family.digits && anchor.family.digits === cand.family.digits) score += 2;
    if (anchor.colourKey && anchor.colourKey === cand.colourKey) score += 3;
    else if (anchor.colourFamily === cand.colourFamily) score += 1;
    if (anchor.family.colourCode && anchor.family.colourCode === cand.family.colourCode) score += 1;
    return score;
  }

  /* Normalise a products.json product ---------------------------------- */
  function normalise(raw, stepKey) {
    var opts = raw.options || [];
    var sizeIdx = -1, fitIdx = -1, colourIdx = -1;
    opts.forEach(function (o, i) {
      var n = String(o.name || '').toLowerCase();
      if (sizeIdx < 0 && /size|waist|chest|collar|uk/.test(n)) sizeIdx = i;
      if (fitIdx < 0 && /^(fit|length|leg)$/.test(n)) fitIdx = i;
      if (colourIdx < 0 && /colou?r/.test(n)) colourIdx = i;
    });
    var styleCode = '';
    var supplierColour = '';
    (raw.tags || []).forEach(function (tag) {
      if (/^Style:\s*/i.test(tag)) styleCode = tag.replace(/^Style:\s*/i, '').trim();
      if (/^Supplier Colour:\s*/i.test(tag)) supplierColour = tag.replace(/^Supplier Colour:\s*/i, '').trim();
    });
    var colour = '';
    if (colourIdx > -1 && opts[colourIdx].values && opts[colourIdx].values.length) colour = opts[colourIdx].values[0];
    if (!colour || /^n\/?a$/i.test(colour)) colour = supplierColour;
    if (!colour) {
      var m = decode(raw.title).match(/[\-–—]\s*([A-Za-z ]+)$/);
      if (m) colour = m[1].trim();
    }
    if (/^n\/?a$/i.test(colour)) colour = '';
    var ci = colourInfo(colour + ' ' + decode(raw.title));
    var variants = (raw.variants || []).map(function (v) {
      var size = sizeIdx > -1 ? v['option' + (sizeIdx + 1)] : (v.title === 'Default Title' ? 'One size' : v.title);
      var fit = fitIdx > -1 ? v['option' + (fitIdx + 1)] : '';
      if (/^n\/?a$/i.test(String(fit))) fit = '';
      return {
        id: v.id,
        size: String(size),
        fit: String(fit || ''),
        available: !!v.available,
        price: parseFloat(v.price) || 0,
        compare: v.compare_at_price ? parseFloat(v.compare_at_price) || 0 : 0
      };
    });
    var avail = variants.filter(function (v) { return v.available; });
    var priceFrom = (avail.length ? avail : variants).reduce(function (min, v) { return min == null || v.price < min ? v.price : min; }, null) || 0;
    var compareFrom = (avail.length ? avail : variants).reduce(function (max, v) { return v.compare > max ? v.compare : max; }, 0);
    var title = decode(raw.title);
    var fitWord = (title.match(/\b(Slim|Tapered|Tailored|Regular|Classic|Modern)\b/i) || [])[1] || '';
    var p = {
      id: raw.id,
      handle: raw.handle,
      title: title,
      vendor: decode(raw.vendor || ''),
      type: raw.product_type || '',
      url: '/products/' + raw.handle,
      image: raw.images && raw.images.length ? raw.images[0].src : (raw.featured_image || ''),
      colour: colour,
      colourKey: normColour(colour),
      colourFamily: ci.family,
      swatch: ci.swatch,
      styleCode: styleCode,
      fitWord: fitWord ? fitWord.charAt(0).toUpperCase() + fitWord.slice(1).toLowerCase() : '',
      variants: variants,
      available: avail.length > 0,
      priceFrom: priceFrom,
      compareFrom: compareFrom > priceFrom ? compareFrom : 0,
      isBoys: /\bboys?\b/i.test(title),
      step: stepKey
    };
    p.family = familyOf(p);
    p.shoeStyle = stepKey === 'shoes' || stepKey === 'shoes_alt' ? shoeStyle(p) : '';
    p.sizes = sortSizes(variants.map(function (v) { return v.size; }).filter(function (v, i, a) { return a.indexOf(v) === i; }));
    p.fits = variants.map(function (v) { return v.fit; }).filter(function (v, i, a) { return v && a.indexOf(v) === i; }).sort(function (a, b) { return fitOrder(a) - fitOrder(b); });
    return p;
  }

  /* ------------------------------------------------------------------ */
  /* Data                                                                */
  /* ------------------------------------------------------------------ */
  var data = {};      // stepKey → { status, products, byHandle }
  function loadStep(step) {
    if (step.key === 'shoes_alt' && SHOES) {
      return loadStep(SHOES).then(function (d) { data['shoes_alt'] = d; return d; });
    }
    var d = data[step.key];
    if (d && (d.status === 'loading' || d.status === 'ready')) return d.promise;
    d = data[step.key] = { status: 'loading', products: [], byHandle: {} };
    d.promise = (function fetchAll(page, acc) {
      var url = '/collections/' + encodeURIComponent(step.handle) + '/products.json?limit=250&page=' + page;
      return fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(function (json) {
          var list = (json && json.products) || [];
          acc = acc.concat(list);
          if (list.length === 250 && page < 6) return fetchAll(page + 1, acc);
          return acc;
        });
    })(1, []).then(function (rawList) {
      d.products = rawList.map(function (r) { return normalise(r, step.key); })
        .filter(function (p) { return cfg.includeBoys || !p.isBoys; });
      d.products.forEach(function (p) { d.byHandle[p.handle] = p; });
      d.status = 'ready';
      return d;
    }).catch(function (err) {
      d.status = 'error';
      d.error = err;
      return d;
    });
    return d.promise;
  }

  /* ------------------------------------------------------------------ */
  /* State                                                               */
  /* ------------------------------------------------------------------ */
  var state = {
    step: 0,                 // index into STEPS; STEPS.length === review
    picks: {},               // key → { handle, variantId, size, fit }
    expanded: null,          // handle open in the size chooser
    expandedStep: null,      // step key the chooser belongs to (upsell uses shoes_alt)
    pendingFit: {},          // handle → chosen fit before size
    filters: {},             // key → { colour, brand, fit, stock }
    sheetOpen: false
  };
  function stepAt(i) { return STEPS[i]; }
  function stepIndex(key) { for (var i = 0; i < STEPS.length; i++) if (STEPS[i].key === key) return i; return -1; }
  function pickProduct(key) {
    var pk = state.picks[key];
    if (!pk || !data[key] || data[key].status !== 'ready') return null;
    return data[key].byHandle[pk.handle] || null;
  }
  function pickVariant(key) {
    var p = pickProduct(key), pk = state.picks[key];
    if (!p || !pk) return null;
    for (var i = 0; i < p.variants.length; i++) if (String(p.variants[i].id) === String(pk.variantId)) return p.variants[i];
    return null;
  }
  function pickedCount() { return Object.keys(state.picks).filter(function (k) { return state.picks[k] && state.picks[k].variantId; }).length; }
  function requiredMet() {
    return STEPS.filter(function (s) { return s.required; }).every(function (s) { return state.picks[s.key] && state.picks[s.key].variantId; });
  }
  function totals() {
    var sub = 0, comp = 0, n = 0;
    STEPS.forEach(function (s) {
      var v = pickVariant(s.key);
      if (v) { sub += v.price; comp += v.compare > v.price ? v.compare : v.price; n++; }
    });
    return { subtotal: sub, compare: comp, save: Math.max(0, comp - sub), count: n };
  }

  function persist() {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify({ picks: state.picks, step: state.step })); } catch (e) { /* noop */ }
    // URL: ?jacket=handle.variantId&trousers=…
    try {
      var url = new URL(window.location.href);
      STEPS.forEach(function (s) {
        var pk = state.picks[s.key];
        if (pk && pk.handle) url.searchParams.set(s.key, pk.handle + (pk.variantId ? '.' + pk.variantId : ''));
        else url.searchParams.delete(s.key);
      });
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) { /* noop */ }
  }
  function restore() {
    var fromUrl = false;
    try {
      var url = new URL(window.location.href);
      STEPS.forEach(function (s) {
        var raw = url.searchParams.get(s.key);
        if (!raw) return;
        var parts = raw.split('.');
        state.picks[s.key] = { handle: parts[0], variantId: parts[1] || null };
        fromUrl = true;
      });
    } catch (e) { /* noop */ }
    if (!fromUrl) {
      try {
        var saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
        if (saved && saved.picks) { state.picks = saved.picks; if (typeof saved.step === 'number') state.step = Math.min(saved.step, STEPS.length); }
      } catch (e) { /* noop */ }
    } else {
      // Land on the first incomplete step (or the product with no variant yet).
      for (var i = 0; i < STEPS.length; i++) {
        var pk = state.picks[STEPS[i].key];
        if (pk && !pk.variantId) { state.step = STEPS[i].hidden ? STEPS.length : i; state.expanded = pk.handle; state.expandedStep = STEPS[i].key; return; }
      }
      state.step = 0;
      for (var j = 0; j < STEPS.length; j++) {
        if (STEPS[j].hidden) continue;
        if (!state.picks[STEPS[j].key]) { state.step = j; return; }
      }
      state.step = STEPS.length;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                           */
  /* ------------------------------------------------------------------ */
  var els = {
    stepper: root.querySelector('[data-sb-stepper]'),
    panel: root.querySelector('[data-sb-panel]'),
    live: root.querySelector('[data-sb-live]'),
    summaryCard: root.querySelector('[data-sb-summary-card]'),
    dock: root.querySelector('[data-sb-dock]'),
    sheet: root.querySelector('[data-sb-sheet]'),
    sheetMount: root.querySelector('[data-sb-sheet-mount]'),
    toast: root.querySelector('[data-sb-toast]')
  };

  function announce(msg) { if (els.live) { els.live.textContent = ''; setTimeout(function () { els.live.textContent = msg; }, 30); } }
  var toastTimer;
  function toast(msg) {
    if (!els.toast) return;
    els.toast.textContent = msg;
    els.toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { els.toast.hidden = true; }, 2600);
  }

  function renderAll() { renderStepper(); renderPanel(); renderSummary(); }

  function renderStepper() {
    var html = '';
    STEPS.forEach(function (s, i) {
      if (s.hidden) return;
      var p = pickProduct(s.key);
      var done = !!(state.picks[s.key] && state.picks[s.key].variantId);
      var current = state.step === i;
      var mark = done && p && p.image
        ? '<img src="' + esc(img(p.image, 120)) + '" alt="">'
        : (done ? '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M5 12l5 5L20 7" stroke-linecap="round" stroke-linejoin="round"/></svg>' : String(i + 1));
      html += '<li class="sb__step' + (done ? ' is-done' : '') + '">' +
        '<button type="button" class="sb__step-btn" data-sb-action="goto" data-index="' + i + '"' + (current ? ' aria-current="step"' : '') + '>' +
        '<span class="sb__step-mark" aria-hidden="true">' + mark + '</span>' +
        '<span class="sb__step-label"><span>' + esc(s.label) + '</span>' +
        (s.required ? '' : '<span class="sb__step-sub">' + esc(t('optional')) + '</span>') +
        '</span></button></li>';
    });
    var reviewCurrent = state.step === STEPS.length;
    html += '<li class="sb__step' + (requiredMet() ? ' is-done' : '') + '">' +
      '<button type="button" class="sb__step-btn" data-sb-action="goto" data-index="' + STEPS.length + '"' + (reviewCurrent ? ' aria-current="step"' : '') + '>' +
      '<span class="sb__step-mark" aria-hidden="true">' + (VISIBLE.length + 1) + '</span>' +
      '<span class="sb__step-label"><span>' + esc(t('review')) + '</span></span></button></li>';
    els.stepper.innerHTML = html;
  }

  function currentFilters(key) { return state.filters[key] || (state.filters[key] = { colour: '', brand: '', fit: '', stock: false }); }

  function applyFilters(list, f, key) {
    return list.filter(function (p) {
      if (f.colour && p.colourKey !== f.colour) return false;
      if (f.brand && p.vendor !== f.brand) return false;
      if (f.fit && p.fitWord !== f.fit) return false;
      if (f.stock) {
        var want = suggestedSize(key, p);
        if (want) {
          var ok = p.variants.some(function (v) { return v.available && v.size === want.size && (!want.fit || !v.fit || v.fit === want.fit); });
          if (!ok) return false;
        } else if (!p.available) return false;
      }
      return true;
    });
  }

  /* Size suggestion for a candidate on step `key`, derived from the jacket. */
  function suggestedSize(key, p) {
    var jv = pickVariant('jacket');
    if (!jv || key === 'jacket') return null;
    var chest = numeric(jv.size);
    if (chest == null) return null;
    var target = null, reason = '';
    if (key === 'trousers') { target = chest - 6; reason = t('suggested_drop', { size: jv.size }); }
    else if (key === 'waistcoat') { target = chest; reason = t('suggested_same'); }
    if (target == null) return null;
    var size = null;
    for (var i = 0; i < p.sizes.length; i++) { if (numeric(p.sizes[i]) === target) { size = p.sizes[i]; break; } }
    if (!size) return null;
    var fit = '';
    if (jv.fit && p.fits.indexOf(jv.fit) > -1) fit = jv.fit;
    return { size: size, fit: fit, reason: reason };
  }

  function groupsFor(step, list) {
    var anchor = pickProduct('jacket');
    var isClothStep = step.key === 'trousers' || step.key === 'waistcoat';
    if (isClothStep && anchor) {
      var a = [], b = [], c = [];
      list.forEach(function (p) {
        var sc = matchScore(anchor, p);
        p._score = sc;
        if (sc >= 8) a.push(p); else if (sc >= 6) b.push(p); else c.push(p);
      });
      var byScore = function (x, y) { return (y._score - x._score) || (y.available - x.available) || (x.priceFrom - y.priceFrom); };
      a.sort(byScore); b.sort(byScore);
      c.sort(function (x, y) {
        var fx = anchor.colourFamily === x.colourFamily ? 1 : 0, fy = anchor.colourFamily === y.colourFamily ? 1 : 0;
        return (fy - fx) || (y.available - x.available) || x.title.localeCompare(y.title);
      });
      var out = [];
      if (a.length) out.push({ label: t('made_to_go'), items: a, match: true, note: anchor.vendor + ' · ' + (anchor.colour || '') });
      if (b.length) out.push({ label: t('same_brand'), items: b });
      if (c.length) out.push({ label: out.length ? t('more_options') : t('everything'), items: c });
      return out;
    }
    if (step.key === 'jacket') {
      var sorted = list.slice().sort(function (x, y) { return (y.available - x.available) || x.vendor.localeCompare(y.vendor) || x.title.localeCompare(y.title); });
      return [{ label: t('everything'), items: sorted }];
    }
    // Shirt / tie / shoes: in-stock first, classic colours first for shirts.
    var pref = step.key === 'shirt' ? ['white', 'blue'] : [];
    var sortedOther = list.slice().sort(function (x, y) {
      var px = pref.indexOf(x.colourFamily), py = pref.indexOf(y.colourFamily);
      px = px < 0 ? 9 : px; py = py < 0 ? 9 : py;
      return (y.available - x.available) || (px - py) || x.title.localeCompare(y.title);
    });
    return [{ label: t('everything'), items: sortedOther }];
  }

  function renderPanel() {
    root.classList.add('is-hydrated');
    if (state.step >= STEPS.length) { renderReview(); return; }
    var step = stepAt(state.step);
    var d = data[step.key];
    var head = '<div class="sb__panel-head">' +
      '<span class="eyebrow">' + esc(t('step_of', { current: VISIBLE.indexOf(step) + 1, total: VISIBLE.length + 1 })) + '</span>' +
      '<h2 class="sb__panel-title" data-sb-title>' + esc(t('choose', { item: step.label.toLowerCase() })) + '</h2>' +
      (step.required ? '' : '<button type="button" class="sb__panel-skip" data-sb-action="skip">' + esc(t('skip')) + '</button>') +
      (step.hint ? '<p class="sb__panel-hint">' + esc(step.hint) + '</p>' : '') +
      '</div>';

    if (!d || d.status === 'loading') {
      els.panel.innerHTML = head + '<div class="sb__skeleton" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i></div><p class="visually-hidden">' + esc(t('loading')) + '</p>';
      return;
    }
    if (d.status === 'error') {
      els.panel.innerHTML = head + '<p class="sb__error">' + esc(t('load_error')) + ' <a href="' + esc(step.url) + '">' + esc(step.label) + '</a><button type="button" class="btn btn--secondary btn--sm" data-sb-action="retry">' + esc(t('retry')) + '</button></p>';
      return;
    }

    var f = currentFilters(step.key);
    var all = d.products;
    var colours = {}, brands = {}, fits = {};
    all.forEach(function (p) {
      if (p.colourKey) colours[p.colourKey] = colours[p.colourKey] || { label: p.colour, swatch: p.swatch, n: 0 };
      if (p.colourKey) colours[p.colourKey].n++;
      if (p.vendor) brands[p.vendor] = (brands[p.vendor] || 0) + 1;
      if (p.fitWord) fits[p.fitWord] = (fits[p.fitWord] || 0) + 1;
    });
    var colourKeys = Object.keys(colours).sort(function (a, b) { return colours[b].n - colours[a].n; });
    var brandKeys = Object.keys(brands).sort();
    var fitKeys = Object.keys(fits).sort();
    var hasSuggestion = !!pickVariant('jacket') && (step.key === 'trousers' || step.key === 'waistcoat');

    var filters = '<div class="sb__filters" role="group" aria-label="Filters">';
    if (colourKeys.length > 1) {
      filters += '<div class="sb__filter"><span class="sb__filter-label">' + esc(t('colour')) + '</span><div class="sb__chips">' +
        '<button type="button" class="sb__chip" data-sb-action="filter" data-filter="colour" data-value="" aria-pressed="' + (!f.colour) + '">' + esc(t('all')) + '</button>' +
        colourKeys.slice(0, 12).map(function (k) {
          return '<button type="button" class="sb__chip" data-sb-action="filter" data-filter="colour" data-value="' + esc(k) + '" aria-pressed="' + (f.colour === k) + '"><span class="sb__swatch" style="--swatch:' + esc(colours[k].swatch) + '"></span>' + esc(colours[k].label) + '</button>';
        }).join('') + '</div></div>';
    }
    if (brandKeys.length > 1) {
      filters += '<div class="sb__filter"><label class="sb__filter-label" for="sb-brand-' + step.key + '">' + esc(t('brand')) + '</label><select id="sb-brand-' + step.key + '" data-sb-action="filter-select" data-filter="brand"><option value="">' + esc(t('all')) + '</option>' +
        brandKeys.map(function (b) { return '<option value="' + esc(b) + '"' + (f.brand === b ? ' selected' : '') + '>' + esc(b) + ' (' + brands[b] + ')</option>'; }).join('') + '</select></div>';
    }
    if (fitKeys.length > 1) {
      filters += '<div class="sb__filter"><label class="sb__filter-label" for="sb-fit-' + step.key + '">' + esc(t('fit')) + '</label><select id="sb-fit-' + step.key + '" data-sb-action="filter-select" data-filter="fit"><option value="">' + esc(t('all')) + '</option>' +
        fitKeys.map(function (b) { return '<option value="' + esc(b) + '"' + (f.fit === b ? ' selected' : '') + '>' + esc(b) + '</option>'; }).join('') + '</select></div>';
    }
    if (hasSuggestion) {
      filters += '<div class="sb__filter sb__filter--toggle"><label><input type="checkbox" data-sb-action="filter-stock"' + (f.stock ? ' checked' : '') + '> ' + esc(t('in_stock_size')) + '</label></div>';
    }
    if (f.colour || f.brand || f.fit || f.stock) {
      filters += '<button type="button" class="sb__link-btn sb__filter-clear" data-sb-action="clear-filters">' + esc(t('clear_filters')) + '</button>';
    }
    filters += '</div>';

    var list = applyFilters(all, f, step.key);
    var groups = groupsFor(step, list);
    var body = '';
    if (!list.length) {
      body = '<p class="sb__empty">' + esc(t('no_results')) + ' <button type="button" class="sb__link-btn" data-sb-action="clear-filters">' + esc(t('clear_filters')) + '</button></p>';
    } else {
      groups.forEach(function (g) {
        body += '<div class="sb__group"><div class="sb__group-head' + (g.match ? ' sb__group-head--match' : '') + '"><span class="eyebrow">' + esc(g.label) + '</span><span class="sb__group-count">' + g.items.length + '</span></div>' +
          (g.note ? '<p class="sb__group-note">' + esc(g.note) + '</p>' : '') +
          '<ul class="sb__grid" role="list">' + g.items.map(function (p, i) { return renderCell(step, p, i, g.match); }).join('') + '</ul></div>';
      });
    }

    if (step.key === 'shoes') body += renderUpsell();
    var prevIdx = state.step - 1;
    var foot = '<div class="sb__panel-foot">' +
      (prevIdx >= 0 ? '<button type="button" class="sb__link-btn" data-sb-action="goto" data-index="' + prevIdx + '">‹ ' + esc(t('back')) + '</button>' : '<span></span>') +
      '<button type="button" class="btn btn--secondary btn--sm" data-sb-action="next">' + esc(nextIndex() >= STEPS.length ? t('review') : t('next') + ' ›') + '</button>' +
      '</div>';

    els.panel.innerHTML = head + filters + body + foot;
  }

  function renderCell(step, p, i, isMatch) {
    var pk = state.picks[step.key];
    var selected = pk && pk.handle === p.handle && pk.variantId;
    var open = state.expanded === p.handle && (state.expandedStep || stateStep().key) === step.key;
    var cls = 'sb__cell' + (open ? ' is-open' : '');
    if (open) return '<li class="' + cls + '" data-handle="' + esc(p.handle) + '">' + renderChooser(step, p) + '</li>';
    var priceHtml = '<span class="sb__card-price">' + esc(money(p.priceFrom)) + '</span>' +
      (p.compareFrom ? '<span class="sb__card-compare">' + esc(money(p.compareFrom)) + '</span>' : '');
    return '<li class="' + cls + '" data-handle="' + esc(p.handle) + '">' +
      '<article class="sb__card' + (selected ? ' is-selected' : '') + '">' +
      '<button type="button" class="sb__card-media" data-sb-action="open" data-step="' + esc(step.key) + '" data-handle="' + esc(p.handle) + '" aria-label="' + esc(t('select') + ': ' + p.title) + '">' +
      (p.image ? '<img src="' + esc(img(p.image, 600)) + '" srcset="' + esc(img(p.image, 400)) + ' 400w, ' + esc(img(p.image, 600)) + ' 600w, ' + esc(img(p.image, 900)) + ' 900w" sizes="(min-width: 1200px) 18vw, (min-width: 641px) 30vw, 45vw" alt="" loading="' + (i < 4 ? 'eager' : 'lazy') + '">' : '') +
      (isMatch ? '<span class="sb__card-badge">' + esc(t('made_to_go')) + '</span>' : '') +
      (!p.available ? '<span class="sb__card-soldout">' + esc(t('sold_out')) + '</span>' : '') +
      '<span class="sb__card-tick" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12l5 5L20 7" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
      '</button>' +
      '<div class="sb__card-info">' +
      (p.vendor ? '<span class="sb__card-vendor">' + esc(p.vendor) + '</span>' : '') +
      '<h3 class="sb__card-title"><a href="' + esc(p.url) + '">' + esc(p.title) + '</a></h3>' +
      '<div class="sb__card-meta">' + priceHtml + '</div>' +
      (p.colour ? '<span class="sb__card-colour"><span class="sb__swatch" style="--swatch:' + esc(p.swatch) + '"></span>' + esc(p.colour) + (p.fitWord ? ' · ' + esc(p.fitWord) : '') + '</span>' : '') +
      '</div>' +
      '<button type="button" class="btn btn--secondary btn--sm sb__card-btn' + (selected ? ' is-selected' : '') + '" data-sb-action="open" data-step="' + esc(step.key) + '" data-handle="' + esc(p.handle) + '"' + (!p.available ? ' disabled' : '') + '>' + esc(selected ? t('selected') + ' · ' + (pk.size || '') + (pk.fit ? ' ' + fitLabel(pk.fit) : '') : (p.available ? t('select') : t('sold_out'))) + '</button>' +
      '</article></li>';
  }

  function renderChooser(step, p) {
    var pk = state.picks[step.key];
    var chosenSize = pk && pk.handle === p.handle ? pk.size : null;
    var chosenFit = pk && pk.handle === p.handle ? pk.fit : (state.pendingFit[p.handle] || '');
    var sug = suggestedSize(step.key, p);
    if (!chosenFit && p.fits.length) chosenFit = (sug && sug.fit) || (p.fits.indexOf('R') > -1 ? 'R' : p.fits[0]);
    var sizeBtns = p.sizes.map(function (sz) {
      var v = p.variants.filter(function (x) { return x.size === sz && (!p.fits.length || x.fit === chosenFit); })[0];
      var avail = !!(v && v.available);
      var isSug = sug && sug.size === sz;
      return '<button type="button" class="sb__size' + (avail ? '' : ' is-unavailable') + (isSug ? ' is-suggested' : '') + '" data-sb-action="size" data-step="' + esc(step.key) + '" data-handle="' + esc(p.handle) + '" data-size="' + esc(sz) + '" data-fit="' + esc(chosenFit) + '" aria-pressed="' + (chosenSize === sz) + '"' + (avail ? '' : ' aria-disabled="true"') + (avail ? '' : ' title="' + esc(t('sold_out')) + '"') + '>' + esc(sz) + '</button>';
    }).join('');
    var fitBtns = p.fits.length ? '<div><div class="sb__choose-label">' + esc(t('length')) + '</div><div class="sb__sizes">' + p.fits.map(function (ft) {
      return '<button type="button" class="sb__size" data-sb-action="fit" data-step="' + esc(step.key) + '" data-handle="' + esc(p.handle) + '" data-fit="' + esc(ft) + '" aria-pressed="' + (chosenFit === ft) + '">' + esc(fitLabel(ft)) + '</button>';
    }).join('') + '</div></div>' : '';
    var sugAvail = !!(sug && p.variants.some(function (x) { return x.available && x.size === sug.size && (!p.fits.length || x.fit === chosenFit); }));
    var priceV = chosenSize ? p.variants.filter(function (x) { return x.size === chosenSize && (!p.fits.length || x.fit === chosenFit); })[0] : null;
    var price = priceV ? priceV.price : p.priceFrom;
    var compare = priceV ? priceV.compare : p.compareFrom;
    return '<div class="sb__choose" data-sb-chooser>' +
      '<div class="sb__choose-media">' + (p.image ? '<img src="' + esc(img(p.image, 500)) + '" alt="' + esc(p.title) + '">' : '') + '</div>' +
      '<div class="sb__choose-body">' +
      '<div class="sb__choose-head"><div>' + (p.vendor ? '<span class="sb__card-vendor">' + esc(p.vendor) + '</span>' : '') + '<h3 class="sb__choose-title">' + esc(p.title) + '</h3>' +
      '<span class="sb__card-colour">' + (p.colour ? '<span class="sb__swatch" style="--swatch:' + esc(p.swatch) + '"></span>' + esc(p.colour) : '') + (p.fitWord ? ' · ' + esc(p.fitWord) : '') + (p.styleCode ? ' · ' + esc(p.styleCode) : '') + '</span>' +
      '</div><button type="button" class="sb__choose-close" data-sb-action="close" data-step="' + esc(step.key) + '" aria-label="' + esc(t('back')) + '">×</button></div>' +
      fitBtns +
      '<div><div class="sb__choose-label">' + esc(t('pick_size')) + (cfg.sizeGuideUrl ? '<a href="' + esc(cfg.sizeGuideUrl) + '" target="_blank" rel="noopener">Size guide</a>' : '') + '</div>' +
      '<div class="sb__sizes" data-sb-sizes>' + sizeBtns + '</div>' +
      (sug ? '<p class="sb__suggest">' + esc(sug.reason) + ' — ' + esc(sug.size) + (sug.fit ? ' ' + esc(fitLabel(sug.fit)) : '') + (sugAvail ? '' : ' (' + esc(t('sold_out').toLowerCase()) + (p.fits.length ? ' · ' + esc(fitLabel(chosenFit)).toLowerCase() : '') + ')') + '</p>' : '') +
      '</div>' +
      '<div class="sb__choose-foot"><span class="sb__choose-price">' + esc(money(price)) + (compare > price ? ' <span class="sb__card-compare">' + esc(money(compare)) + '</span>' : '') + '</span>' +
      '<a class="sb__link-btn" href="' + esc(p.url) + '">' + esc(t('view_product')) + '</a>' +
      (chosenSize ? '<button type="button" class="sb__link-btn sb__link-btn--quiet" data-sb-action="remove" data-step="' + esc(step.key) + '">' + esc(t('remove')) + '</button>' : '') +
      '</div></div></div>';
  }

  /* Shoes upsell — the opposite style to the pair already chosen. */
  function upsellStyle() {
    var shoes = pickProduct('shoes');
    if (!UPSELL || !shoes || !pickVariant('shoes')) return null;
    return shoes.shoeStyle === 'comfy' ? 'formal' : 'comfy';
  }
  function renderUpsell() {
    var want = upsellStyle();
    if (!want) return '';
    var d = data['shoes_alt'] || data['shoes'];
    if (!d || d.status !== 'ready') return '';
    if (pickProduct('shoes_alt') && pickVariant('shoes_alt')) return '';
    var shoes = pickProduct('shoes');
    var list = d.products.filter(function (p) { return p.shoeStyle === want && p.available && p.handle !== shoes.handle; });
    if (!list.length) return '';
    list.sort(function (x, y) {
      var cx = x.colourFamily === shoes.colourFamily ? 0 : 1, cy = y.colourFamily === shoes.colourFamily ? 0 : 1;
      return (cx - cy) || (Math.abs(x.priceFrom - shoes.priceFrom) - Math.abs(y.priceFrom - shoes.priceFrom));
    });
    var openHere = state.expandedStep === 'shoes_alt' ? state.expanded : null;
    var shown = list.slice(0, cfg.shoesUpsellLimit || 4);
    if (openHere && !shown.some(function (p) { return p.handle === openHere; })) {
      var extra = d.byHandle[openHere]; if (extra) shown.unshift(extra);
    }
    var heading = want === 'comfy' ? t('upsell_comfy_heading') : t('upsell_formal_heading');
    var sub = want === 'comfy' ? t('upsell_comfy_sub') : t('upsell_formal_sub');
    return '<div class="sb__upsell" data-sb-upsell>' +
      '<div class="sb__group-head sb__group-head--match"><span class="eyebrow">' + esc(t('second_pair')) + '</span><span class="sb__group-count">' + list.length + '</span></div>' +
      '<h3 class="sb__upsell-title">' + esc(heading) + '</h3><p class="sb__group-note">' + esc(sub) + '</p>' +
      '<ul class="sb__grid" role="list">' + shown.map(function (p, i) { return renderCell(UPSELL, p, i, false); }).join('') + '</ul>' +
      '<p class="sb__upsell-foot"><a class="sb__link-btn" href="' + esc(SHOES.url || '#') + '">' + esc(t('upsell_more')) + '</a></p>' +
      '</div>';
  }

  function renderReview() {
    var tot = totals();
    var html = '<div class="sb__panel-head"><span class="eyebrow">' + esc(t('step_of', { current: VISIBLE.length + 1, total: VISIBLE.length + 1 })) + '</span>' +
      '<h2 class="sb__panel-title" data-sb-title>' + esc(t('review')) + '</h2><p class="sb__panel-hint">' + esc(tot.count ? t('review_intro') : t('review_empty')) + '</p></div>';
    if (tot.count) html += boardHTML(tot);
    html += renderUpsell();
    html += looksHTML();
    html += '<div class="sb__review">';
    STEPS.forEach(function (s, i) {
      var p = pickProduct(s.key), v = pickVariant(s.key), pk = state.picks[s.key];
      if (s.hidden && !(p && v)) return;
      if (p && v) {
        html += '<div class="sb__review-item">' +
          (p.image ? '<img src="' + esc(img(p.image, 200)) + '" alt="">' : '<span class="sb__thumb"></span>') +
          '<div><span class="sb__review-step">' + esc(s.label) + (p.vendor ? ' · ' + esc(p.vendor) : '') + '</span>' +
          '<h3 class="sb__review-title"><a href="' + esc(p.url) + '">' + esc(p.title) + '</a></h3>' +
          '<span class="sb__review-size">' + esc(t('size')) + ' ' + esc(v.size) + (v.fit ? ' · ' + esc(fitLabel(v.fit)) : '') + (p.colour ? ' · ' + esc(p.colour) : '') + '</span>' +
          '<div class="sb__review-actions"><button type="button" class="sb__link-btn" data-sb-action="change" data-step="' + esc(s.key) + '">' + esc(t('change')) + '</button><button type="button" class="sb__link-btn sb__link-btn--quiet" data-sb-action="remove" data-step="' + esc(s.key) + '">' + esc(t('remove')) + '</button></div></div>' +
          '<div class="sb__review-price">' + esc(money(v.price)) + (v.compare > v.price ? '<br><span class="sb__card-compare">' + esc(money(v.compare)) + '</span>' : '') + '</div></div>';
      } else {
        html += '<div class="sb__review-item sb__review-item--empty"><span class="sb__thumb"></span><div><span class="sb__review-step">' + esc(s.label) + (s.required ? '' : ' · ' + esc(t('optional'))) + '</span>' +
          '<h3 class="sb__review-title"><button type="button" class="sb__link-btn" data-sb-action="goto" data-index="' + i + '">' + esc(t('choose', { item: s.label.toLowerCase() })) + '</button></h3></div></div>';
      }
    });
    html += '</div>';
    els.panel.innerHTML = html;
  }

  /* ------------------------------------------------------------------ */
  /* The look board + saved looks                                        */
  /* ------------------------------------------------------------------ */
  function lookName() {
    var j = pickProduct('jacket');
    if (!j) return t('your_suit');
    var pieces = pickedCount();
    var kind = pieces >= 3 && pickVariant('waistcoat') ? t('three_piece') : t('two_piece');
    return [j.vendor, j.colour, kind].filter(Boolean).join(' · ');
  }
  function boardHTML(tot) {
    var tiles = '';
    STEPS.forEach(function (s) {
      var p = pickProduct(s.key), v = pickVariant(s.key);
      if (s.hidden && !(p && v)) return;
      var lower = s.label.toLowerCase();
      var arrows = '<button type="button" class="sb__flick sb__flick--prev" data-sb-action="flick" data-step="' + esc(s.key) + '" data-dir="-1" aria-label="' + esc(t('flick_prev', { item: lower })) + '">‹</button>' +
        '<button type="button" class="sb__flick sb__flick--next" data-sb-action="flick" data-step="' + esc(s.key) + '" data-dir="1" aria-label="' + esc(t('flick_next', { item: lower })) + '">›</button>';
      if (p && v) {
        tiles += '<figure class="sb__tile sb__tile--' + esc(s.key) + '" data-sb-tile="' + esc(s.key) + '">' +
          '<div class="sb__tile-media">' + (p.image ? '<img src="' + esc(img(p.image, s.key === 'jacket' ? 900 : 500)) + '" alt="' + esc(p.title) + '">' : '<span class="sb__thumb"></span>') + arrows + '</div>' +
          '<figcaption class="sb__tile-cap"><span class="sb__tile-step">' + esc(s.label) + '</span><span class="sb__tile-title">' + esc(p.vendor ? p.vendor + ' · ' : '') + esc(p.colour || p.title) + '</span><span class="sb__tile-size">' + esc(v.size) + (v.fit ? ' ' + esc(fitLabel(v.fit)) : '') + ' · ' + esc(money(v.price)) + '</span></figcaption></figure>';
      } else {
        tiles += '<figure class="sb__tile sb__tile--empty sb__tile--' + esc(s.key) + '" data-sb-tile="' + esc(s.key) + '">' +
          '<div class="sb__tile-media"><button type="button" class="sb__tile-try" data-sb-action="flick" data-step="' + esc(s.key) + '" data-dir="1">+ ' + esc(t('try_item', { item: lower })) + '</button></div>' +
          '<figcaption class="sb__tile-cap"><span class="sb__tile-step">' + esc(s.label) + (s.required ? '' : ' · ' + esc(t('optional'))) + '</span></figcaption></figure>';
      }
    });
    var saved = looksMatchingCurrent();
    return '<section class="sb__board" aria-labelledby="sb-board-title">' +
      '<div class="sb__board-head"><div><span class="eyebrow">' + esc(t('board_title')) + '</span><h3 id="sb-board-title" class="sb__board-name">' + esc(lookName()) + '</h3>' +
      '<p class="sb__board-hint">' + esc(t('board_hint')) + '</p></div>' +
      '<div class="sb__board-meta"><span class="sb__board-total">' + esc(money(tot.subtotal)) + '</span><span class="sb__board-count">' + esc(t('items_count', { count: tot.count, total: VISIBLE.length })) + '</span></div></div>' +
      '<div class="sb__collage">' + tiles + '</div>' +
      '<div class="sb__board-actions">' +
      '<button type="button" class="btn btn--secondary btn--sm sb__save-look" data-sb-action="save-look"' + (saved ? ' aria-pressed="true"' : '') + '>' + '<span class="sb__heart" aria-hidden="true">' + (saved ? '♥' : '♡') + '</span> ' + esc(saved ? t('look_saved') : t('save_look')) + '</button>' +
      '<button type="button" class="sb__link-btn" data-sb-action="share">' + esc(t('copy_link')) + '</button>' +
      '</div></section>';
  }
  function looksHTML() {
    var looks = loadLooks();
    if (!looks.length) return '';
    var curSig = lookSig(state.picks);
    var curIdx = -1;
    looks.forEach(function (l, i) { if (l.sig === curSig) curIdx = i; });
    var cards = looks.map(function (l, i) {
      var keys = STEPS.map(function (s) { return s.key; }).filter(function (k) { return l.images[k]; });
      var minis = keys.slice(0, 4).map(function (k) { return '<img src="' + esc(img(l.images[k], 240)) + '" alt="">'; }).join('');
      var isCur = i === curIdx;
      return '<li class="sb__look' + (isCur ? ' is-current' : '') + '">' +
        '<button type="button" class="sb__look-btn" data-sb-action="load-look" data-id="' + esc(l.id) + '"' + (isCur ? ' aria-current="true"' : '') + '>' +
        '<span class="sb__look-collage sb__look-collage--' + Math.min(4, Math.max(1, keys.length)) + '">' + minis + '</span>' +
        '<span class="sb__look-cap"><span class="sb__look-n">' + esc(t('look_name', { n: i + 1 })) + (isCur ? ' · ' + esc(t('current_look')) : '') + '</span><span class="sb__look-name">' + esc(l.name) + '</span><span class="sb__look-total">' + esc(money(l.total)) + ' · ' + esc(t('items_count', { count: l.count, total: VISIBLE.length })) + '</span></span></button>' +
        '<button type="button" class="sb__look-del" data-sb-action="delete-look" data-id="' + esc(l.id) + '" aria-label="' + esc(t('delete_look')) + ' ' + esc(t('look_name', { n: i + 1 })) + '">×</button></li>';
    }).join('');
    return '<section class="sb__looks" aria-labelledby="sb-looks-title">' +
      '<div class="sb__looks-head"><div><span class="eyebrow">' + esc(t('saved_looks')) + '</span><h3 id="sb-looks-title" class="sb__looks-title">' + esc(t('looks_hint')) + '</h3></div>' +
      (looks.length > 1 ? '<div class="sb__looks-nav"><button type="button" class="sb__flick" data-sb-action="look-step" data-dir="-1" aria-label="' + esc(t('prev_look')) + '">‹</button><button type="button" class="sb__flick" data-sb-action="look-step" data-dir="1" aria-label="' + esc(t('next_look')) + '">›</button></div>' : '') +
      '</div><ul class="sb__looks-rail" role="list" data-sb-looks-rail>' + cards + '</ul></section>';
  }
  function lookSig(picks) {
    return STEPS.map(function (s) { var pk = picks[s.key]; return pk && pk.variantId ? s.key + '=' + pk.handle + '.' + pk.variantId : ''; }).filter(Boolean).join('&');
  }
  function loadLooks() {
    try { var arr = JSON.parse(localStorage.getItem(LOOKS_KEY) || '[]'); return Array.isArray(arr) ? arr : []; } catch (e) { return []; }
  }
  function storeLooks(arr) { try { localStorage.setItem(LOOKS_KEY, JSON.stringify(arr.slice(0, 12))); } catch (e) { /* noop */ } }
  function looksMatchingCurrent() {
    var sig = lookSig(state.picks);
    return sig ? loadLooks().filter(function (l) { return l.sig === sig; })[0] || null : null;
  }
  function saveLook() {
    if (!pickedCount()) return;
    var sig = lookSig(state.picks);
    var looks = loadLooks();
    var existing = looks.filter(function (l) { return l.sig === sig; })[0];
    if (existing) { looks = looks.filter(function (l) { return l.sig !== sig; }); storeLooks(looks); toast(t('look_removed')); announce(t('look_removed')); renderPanel(); return; }
    var images = {}, picks = {};
    STEPS.forEach(function (s) {
      var p = pickProduct(s.key), pk = state.picks[s.key];
      if (p && pk && pk.variantId) { picks[s.key] = { handle: pk.handle, variantId: pk.variantId, size: pk.size, fit: pk.fit }; if (p.image) images[s.key] = p.image; }
    });
    var tot = totals();
    looks.push({ id: 'l' + Date.now().toString(36), sig: sig, name: lookName(), picks: picks, images: images, total: tot.subtotal, count: tot.count, ts: Date.now() });
    storeLooks(looks);
    push('suit_builder_save_look', { items: tot.count, value: tot.subtotal });
    toast(t('look_saved')); announce(t('look_saved'));
    renderPanel();
    var rail = els.panel.querySelector('[data-sb-looks-rail]');
    if (rail) rail.scrollLeft = rail.scrollWidth;
  }
  function deleteLook(id) {
    storeLooks(loadLooks().filter(function (l) { return l.id !== id; }));
    renderPanel();
  }
  function loadLook(id) {
    var look = loadLooks().filter(function (l) { return l.id === id; })[0];
    if (!look) return;
    state.picks = {};
    Object.keys(look.picks).forEach(function (k) { if (stepIndex(k) > -1) state.picks[k] = Object.assign({}, look.picks[k]); });
    state.expanded = null;
    state.step = STEPS.length;
    renderAll();
    Promise.all(STEPS.filter(function (s) { return state.picks[s.key]; }).map(loadStep)).then(function () {
      STEPS.forEach(function (s) {
        var pk = state.picks[s.key], v = pickVariant(s.key);
        if (pk && v) { pk.size = v.size; pk.fit = v.fit; }
        else if (pk) delete state.picks[s.key];
      });
      persist(); renderAll();
      announce(look.name);
      push('suit_builder_load_look', { items: pickedCount() });
    });
  }
  function stepLook(dir) {
    var looks = loadLooks();
    if (!looks.length) return;
    var sig = lookSig(state.picks), idx = -1;
    looks.forEach(function (l, i) { if (l.sig === sig) idx = i; });
    var next = idx < 0 ? (dir > 0 ? 0 : looks.length - 1) : (idx + dir + looks.length) % looks.length;
    loadLook(looks[next].id);
  }
  // Flick one piece through its alternatives (match order), keeping the size where possible.
  function flick(key, dir) {
    var idx = stepIndex(key);
    if (idx < 0) return;
    var step = STEPS[idx];
    var tile = els.panel.querySelector('[data-sb-tile="' + key + '"]');
    if (tile) tile.classList.add('is-loading');
    loadStep(step).then(function () {
      var list = data[key].products;
      if (!list || !list.length) return;
      var ordered = [];
      groupsFor(step, list).forEach(function (g) { ordered = ordered.concat(g.items); });
      var pk = state.picks[key];
      var wantSize = pk && pk.size, wantFit = pk && pk.fit;
      var variantFor = function (p) {
        var same = wantSize ? p.variants.filter(function (v) { return v.available && v.size === wantSize && (!wantFit || !v.fit || v.fit === wantFit); })[0] : null;
        return same || (wantSize ? null : p.variants.filter(function (v) { return v.available; })[0]);
      };
      var cur = pk ? ordered.map(function (p) { return p.handle; }).indexOf(pk.handle) : -1;
      var n = ordered.length, found = null, fv = null;
      for (var i = 1; i <= n; i++) {
        var cand = ordered[((cur < 0 ? (dir > 0 ? -1 : 0) : cur) + dir * i + n * 2) % n];
        if (pk && cand.handle === pk.handle) continue;
        var v = variantFor(cand);
        if (v) { found = cand; fv = v; break; }
      }
      if (!found) { toast(t('no_alt', { item: step.label.toLowerCase() })); if (tile) tile.classList.remove('is-loading'); return; }
      state.picks[key] = { handle: found.handle, variantId: fv.id, size: fv.size, fit: fv.fit };
      persist();
      push('suit_builder_flick', { step: key, handle: found.handle, variant_id: fv.id });
      renderAll();
      announce(step.label + ': ' + found.title + ' ' + fv.size);
      var again = els.panel.querySelector('[data-sb-tile="' + key + '"] [data-dir="' + dir + '"]');
      if (again) again.focus({ preventScroll: true });
    });
  }

  function summaryHTML() {
    var tot = totals();
    var list = STEPS.map(function (s, i) {
      var p = pickProduct(s.key), v = pickVariant(s.key), pk = state.picks[s.key];
      if (s.hidden && !(pk && pk.handle)) return '';
      if (p && v) {
        return '<li class="sb__sum-item"><span class="sb__sum-thumb">' + (p.image ? '<img src="' + esc(img(p.image, 160)) + '" alt="">' : '') + '</span>' +
          '<div><span class="sb__sum-step">' + esc(s.label) + '</span><p class="sb__sum-title">' + esc(p.title) + '</p><span class="sb__sum-size">' + esc(v.size) + (v.fit ? ' · ' + esc(fitLabel(v.fit)) : '') + '</span></div>' +
          '<div class="sb__sum-right"><span class="sb__sum-price">' + esc(money(v.price)) + '</span><span class="sb__sum-links"><button type="button" class="sb__link-btn" data-sb-action="change" data-step="' + esc(s.key) + '">' + esc(t('change')) + '</button><button type="button" class="sb__link-btn sb__link-btn--quiet" data-sb-action="remove" data-step="' + esc(s.key) + '" aria-label="' + esc(t('remove') + ' ' + s.label) + '">×</button></span></div></li>';
      }
      if (pk && pk.handle && !v && data[s.key] && data[s.key].status === 'ready') {
        // Product chosen but no size yet.
        var pp = data[s.key].byHandle[pk.handle];
        if (pp) return '<li class="sb__sum-item"><span class="sb__sum-thumb">' + (pp.image ? '<img src="' + esc(img(pp.image, 160)) + '" alt="">' : '') + '</span><div><span class="sb__sum-step">' + esc(s.label) + '</span><p class="sb__sum-title">' + esc(pp.title) + '</p><span class="sb__sum-size">' + esc(t('pick_size')) + '</span></div><div class="sb__sum-right"><button type="button" class="sb__link-btn" data-sb-action="change" data-step="' + esc(s.key) + '">' + esc(t('size')) + '</button></div></li>';
      }
      return '<li class="sb__sum-item sb__sum-item--empty"><span class="sb__sum-thumb" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 5v14M5 12h14" stroke-linecap="round"/></svg></span>' +
        '<div><span class="sb__sum-step">' + esc(s.label) + (s.required ? '' : ' · ' + esc(t('optional'))) + '</span><p class="sb__sum-title"><button type="button" class="sb__link-btn" data-sb-action="goto" data-index="' + i + '">' + esc(t('choose', { item: s.label.toLowerCase() })) + '</button></p></div></li>';
    }).join('');
    var totalsHtml = tot.count ? '<div class="sb__summary-totals">' +
      '<div class="sb__total-row"><span>' + esc(t('items_count', { count: tot.count, total: VISIBLE.length })) + '</span><span></span></div>' +
      (tot.save > 0 ? '<div class="sb__total-row sb__total-row--save"><span>' + esc(t('you_save')) + '</span><span>' + esc(money(tot.save)) + '</span></div>' : '') +
      '<div class="sb__total-row sb__total-row--grand"><span>' + esc(t('subtotal')) + '</span><span>' + esc(money(tot.subtotal)) + '</span></div></div>' : '';
    var ready = requiredMet();
    return '<div class="sb__summary-head"><span class="eyebrow">' + esc(t('your_suit')) + '</span><span class="sb__summary-count">' + esc(t('items_count', { count: tot.count, total: VISIBLE.length })) + '</span></div>' +
      '<ul class="sb__summary-list" role="list">' + list + '</ul>' + totalsHtml +
      '<div class="sb__summary-actions"><button type="button" class="basket-btn sb__add" data-sb-action="add"' + (ready ? '' : ' disabled') + '>' + basketIcon() + '<span class="basket-btn__label">' + esc(t('add_suit')) + (tot.count ? ' · ' + esc(money(tot.subtotal)) : '') + '</span></button>' +
      (!ready ? '<p class="sb__choose-note">' + esc(t('needs_jacket')) + '</p>' : '') +
      '<p class="sb__summary-error" data-sb-add-error role="alert" hidden></p>' +
      '<div class="sb__summary-links"><button type="button" class="sb__link-btn" data-sb-action="share">' + esc(t('copy_link')) + '</button><a class="sb__link-btn" href="' + esc(cfg.bookingUrl || '#') + '">' + esc(t('book_fitting')) + '</a>' +
      (tot.count || pickedCount() ? '<button type="button" class="sb__link-btn sb__link-btn--quiet" data-sb-action="reset">' + esc(t('start_over')) + '</button>' : '') + '</div></div>' +
      '<p class="sb__summary-trust">' + esc(t('trust')) + '</p>';
  }

  function renderSummary() {
    var html = summaryHTML();
    if (els.summaryCard) els.summaryCard.innerHTML = html;
    if (state.sheetOpen && els.sheetMount) els.sheetMount.innerHTML = '<div class="sb__summary">' + html + '</div>';
    // Mobile dock
    if (els.dock) {
      var tot = totals();
      var show = pickedCount() > 0 || Object.keys(state.picks).length > 0;
      els.dock.hidden = !show;
      document.body.classList.toggle('sb-dock-on', show && window.innerWidth < 1024);
      var cnt = els.dock.querySelector('[data-sb-dock-count]'); if (cnt) cnt.textContent = t('items_count', { count: tot.count, total: VISIBLE.length });
      var tl = els.dock.querySelector('[data-sb-dock-total]'); if (tl) tl.textContent = tot.count ? money(tot.subtotal) : '';
      var ab = els.dock.querySelector('[data-sb-add-mobile]'); if (ab) ab.disabled = !requiredMet();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Actions                                                             */
  /* ------------------------------------------------------------------ */
  function focusPanelTitle() {
    var h = els.panel.querySelector('[data-sb-title]');
    if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
    var top = els.stepper.getBoundingClientRect().top + window.scrollY - (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-offset')) || 0) - 12;
    if (window.scrollY > top) window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
  }
  function goto(i, opts) {
    i = Math.max(0, Math.min(STEPS.length, i));
    while (i < STEPS.length && STEPS[i].hidden) i++;
    state.step = i;
    state.expanded = null;
    state.expandedStep = null;
    if (i < STEPS.length) loadStep(STEPS[i]).then(function () { if (state.step === i) { renderPanel(); renderSummary(); } });
    renderAll();
    if (!(opts && opts.quiet)) focusPanelTitle();
    persist();
    push('suit_builder_step', { step: i < STEPS.length ? STEPS[i].key : 'review' });
    // Warm the next step.
    if (i + 1 < STEPS.length) loadStep(STEPS[i + 1]);
  }
  function nextIndex() { var i = state.step + 1; while (i < STEPS.length && STEPS[i].hidden) i++; return i; }
  function stepByKey(key) { for (var i = 0; i < STEPS.length; i++) if (STEPS[i].key === key) return STEPS[i]; return null; }
  function openProduct(handle, stepKey) {
    var step = (stepKey && stepByKey(stepKey)) || stepAt(Math.min(state.step, STEPS.length - 1));
    state.expanded = handle;
    state.expandedStep = step.key;
    var pk = state.picks[step.key];
    if (!pk || pk.handle !== handle) { state.picks[step.key] = { handle: handle, variantId: null }; }
    renderPanel(); renderSummary();
    var cell = els.panel.querySelector('.sb__cell.is-open');
    if (cell) {
      var first = cell.querySelector('.sb__size[aria-pressed="true"]') || cell.querySelector('.sb__size:not(.is-unavailable)') || cell.querySelector('.sb__choose-close');
      var y = cell.getBoundingClientRect().top + window.scrollY - (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-offset')) || 0) - 16;
      window.scrollTo({ top: y, behavior: reduceMotion ? 'auto' : 'smooth' });
      if (first) first.focus({ preventScroll: true });
    }
  }
  function closeChooser() {
    var step = (state.expandedStep && stepByKey(state.expandedStep)) || stateStep();
    var pk = state.picks[step.key];
    if (pk && !pk.variantId) delete state.picks[step.key];
    state.expanded = null;
    state.expandedStep = null;
    renderAll(); persist();
  }
  function stateStep() { return stepAt(Math.min(state.step, STEPS.length - 1)); }
  function chooseSize(handle, size, fit, stepKey) {
    var step = (stepKey && stepByKey(stepKey)) || stateStep();
    var p = data[step.key] && data[step.key].byHandle[handle];
    if (!p) return;
    var v = p.variants.filter(function (x) { return x.size === size && (!p.fits.length || x.fit === fit); })[0];
    if (!v || !v.available) return;
    state.picks[step.key] = { handle: handle, variantId: v.id, size: v.size, fit: v.fit };
    delete state.pendingFit[handle];
    state.expanded = null;
    state.expandedStep = null;
    persist();
    push('suit_builder_pick', { step: step.key, handle: handle, variant_id: v.id, price: v.price });
    if (step.hidden) { announce(step.label + ' ' + t('selected')); toast(t('upsell_added')); renderAll(); return; }
    var nxt = nextIndex();
    var nextStep = nxt < STEPS.length ? STEPS[nxt] : null;
    announce(step.label + ' ' + t('selected') + ': ' + p.title + ' ' + v.size);
    if (cfg.autoAdvance !== false) {
      toast(nextStep ? t('picked_toast', { item: step.label, next: nextStep.label.toLowerCase() }) : t('complete_toast'));
      setTimeout(function () { goto(nxt); }, reduceMotion ? 0 : 350);
    } else {
      renderAll();
    }
  }
  function removePick(key) {
    delete state.picks[key];
    persist(); renderAll();
  }
  // Basket button (shared .basket-btn, critical.css): the icon slot comes
  // from the Liquid-rendered dock button (snippets/basket-btn-icon.liquid),
  // which is never re-rendered, so the SVG lives in one place.
  function basketIcon() {
    var icon = root.querySelector('.basket-btn__icon');
    return icon ? icon.outerHTML : '';
  }
  function setAddLabel(b, text) {
    var label = b.querySelector('.basket-btn__label');
    if (label) label.textContent = text; else b.textContent = text;
  }
  function addToBasket(btn) {
    if (!requiredMet()) return;
    var items = [];
    STEPS.forEach(function (s) { var v = pickVariant(s.key); if (v) items.push({ id: v.id, quantity: 1 }); });
    if (!items.length) return;
    var buttons = root.querySelectorAll('[data-sb-action="add"], [data-sb-add-mobile]');
    var errEls = root.querySelectorAll('[data-sb-add-error]');
    Array.prototype.forEach.call(buttons, function (b) { b.disabled = true; b.classList.add('is-loading'); b.setAttribute('aria-busy', 'true'); setAddLabel(b, t('adding')); });
    Array.prototype.forEach.call(errEls, function (e) { e.hidden = true; e.textContent = ''; });
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items, sections: 'cart-drawer-items', sections_url: window.location.pathname })
    }).then(function (res) { return res.json().then(function (d) { return { ok: res.ok, data: d }; }); })
      .then(function (r) {
        if (!r.ok) throw new Error((r.data && (r.data.description || r.data.message)) || t('add_error'));
        var first = r.data && r.data.items && r.data.items[0] ? r.data.items[0] : r.data;
        document.dispatchEvent(new CustomEvent('cart:added', { detail: { item: first, items: r.data.items || [first], sections: r.data.sections || null } }));
        push('suit_builder_add', { items: items.length, value: totals().subtotal });
        Array.prototype.forEach.call(buttons, function (b) { b.classList.remove('is-loading'); b.classList.add('is-added'); setAddLabel(b, t('added')); });
        announce(t('added'));
        setTimeout(function () {
          // The dock button isn't re-rendered by renderSummary — reset it here
          // (it used to stay "added" and disabled until a reload).
          Array.prototype.forEach.call(root.querySelectorAll('[data-sb-add-mobile]'), function (b) {
            b.classList.remove('is-added'); b.removeAttribute('aria-busy'); setAddLabel(b, t('add_suit_short'));
          });
          renderSummary();
        }, 1800);
      })
      .catch(function (err) {
        Array.prototype.forEach.call(errEls, function (e) { e.textContent = (err && err.message) || t('add_error'); e.hidden = false; });
        Array.prototype.forEach.call(buttons, function (b) { b.classList.remove('is-loading'); b.disabled = false; b.removeAttribute('aria-busy'); if (b.matches('[data-sb-add-mobile]')) setAddLabel(b, t('add_suit_short')); });
        renderSummary();
      });
  }
  function share() {
    persist();
    var url = window.location.href;
    var done = function () { toast(t('copied')); announce(t('copied')); };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, function () { window.prompt(t('copy_link'), url); });
    else window.prompt(t('copy_link'), url);
  }
  function reset() {
    state.picks = {}; state.expanded = null; state.pendingFit = {}; state.filters = {};
    try { sessionStorage.removeItem(STORE_KEY); } catch (e) { /* noop */ }
    goto(0);
  }
  function openSheet() {
    state.sheetOpen = true;
    els.sheet.hidden = false;
    renderSummary();
    var open = els.dock.querySelector('[data-sb-dock-open]'); if (open) open.setAttribute('aria-expanded', 'true');
    var close = els.sheet.querySelector('[data-sb-sheet-close].sb__sheet-close'); if (close) close.focus();
    document.body.style.overflow = 'hidden';
  }
  function closeSheet() {
    state.sheetOpen = false;
    els.sheet.hidden = true;
    var open = els.dock.querySelector('[data-sb-dock-open]'); if (open) { open.setAttribute('aria-expanded', 'false'); open.focus(); }
    document.body.style.overflow = '';
  }

  /* ------------------------------------------------------------------ */
  /* Events                                                              */
  /* ------------------------------------------------------------------ */
  root.addEventListener('click', function (e) {
    var el = e.target.closest('[data-sb-action], [data-sb-dock-open], [data-sb-sheet-close], [data-sb-add-mobile]');
    if (!el || !root.contains(el)) return;
    if (el.hasAttribute('data-sb-dock-open')) { e.preventDefault(); openSheet(); return; }
    if (el.hasAttribute('data-sb-sheet-close')) { e.preventDefault(); closeSheet(); return; }
    if (el.hasAttribute('data-sb-add-mobile')) { e.preventDefault(); addToBasket(el); return; }
    var action = el.getAttribute('data-sb-action');
    if (action === 'filter-select' || action === 'filter-stock') return; // change events
    e.preventDefault();
    var step = stateStep();
    switch (action) {
      case 'goto': if (state.sheetOpen) closeSheet(); goto(parseInt(el.getAttribute('data-index'), 10)); break;
      case 'next': goto(nextIndex()); break;
      case 'skip': goto(nextIndex()); break;
      case 'open': openProduct(el.getAttribute('data-handle'), el.getAttribute('data-step')); break;
      case 'close': closeChooser(); break;
      case 'size': chooseSize(el.getAttribute('data-handle'), el.getAttribute('data-size'), el.getAttribute('data-fit'), el.getAttribute('data-step')); break;
      case 'fit':
        state.pendingFit[el.getAttribute('data-handle')] = el.getAttribute('data-fit');
        var fitStep = el.getAttribute('data-step') || step.key;
        var pk = state.picks[fitStep];
        if (pk && pk.handle === el.getAttribute('data-handle') && pk.variantId) { delete state.picks[fitStep]; state.picks[fitStep] = { handle: pk.handle, variantId: null }; }
        renderPanel();
        var fb = els.panel.querySelector('.sb__size[data-sb-action="fit"][aria-pressed="true"]'); if (fb) fb.focus();
        break;
      case 'change':
        if (state.sheetOpen) closeSheet();
        var ckey = el.getAttribute('data-step');
        var idx = stepIndex(ckey);
        var keep = state.picks[ckey];
        if (STEPS[idx] && STEPS[idx].hidden) {
          // Second pair lives in the upsell on the review step — re-open it there.
          delete state.picks[ckey];
          goto(STEPS.length, { quiet: true });
          loadStep(STEPS[idx]).then(function () { if (keep) openProduct(keep.handle, ckey); });
          break;
        }
        goto(idx, { quiet: true });
        loadStep(STEPS[idx]).then(function () { if (state.step === idx && keep) openProduct(keep.handle); });
        break;
      case 'remove': removePick(el.getAttribute('data-step')); break;
      case 'add': addToBasket(el); break;
      case 'share': share(); break;
      case 'save-look': saveLook(); break;
      case 'load-look': loadLook(el.getAttribute('data-id')); break;
      case 'delete-look': deleteLook(el.getAttribute('data-id')); break;
      case 'look-step': stepLook(parseInt(el.getAttribute('data-dir'), 10)); break;
      case 'flick': flick(el.getAttribute('data-step'), parseInt(el.getAttribute('data-dir'), 10) || 1); break;
      case 'reset': if (state.sheetOpen) closeSheet(); reset(); break;
      case 'retry': data[step.key] = null; loadStep(step).then(function () { renderPanel(); }); renderPanel(); break;
      case 'filter':
        currentFilters(step.key)[el.getAttribute('data-filter')] = el.getAttribute('data-value') || '';
        renderPanel();
        break;
      case 'clear-filters': state.filters[step.key] = { colour: '', brand: '', fit: '', stock: false }; renderPanel(); break;
      default: break;
    }
  });
  root.addEventListener('change', function (e) {
    var el = e.target.closest('[data-sb-action="filter-select"], [data-sb-action="filter-stock"]');
    if (!el) return;
    var step = stateStep();
    var f = currentFilters(step.key);
    if (el.getAttribute('data-sb-action') === 'filter-stock') f.stock = !!el.checked;
    else f[el.getAttribute('data-filter')] = el.value;
    renderPanel();
    var again = els.panel.querySelector('#' + (el.id || '__none__')); if (again) again.focus();
  });
  els.panel.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    var tile = e.target.closest('[data-sb-tile]');
    var rail = e.target.closest('[data-sb-looks-rail]');
    if (!tile && !rail) return;
    e.preventDefault();
    var dir = e.key === 'ArrowRight' ? 1 : -1;
    if (tile) flick(tile.getAttribute('data-sb-tile'), dir); else stepLook(dir);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      if (state.sheetOpen) { closeSheet(); return; }
      if (state.expanded) { closeChooser(); }
    }
  });
  window.addEventListener('resize', function () { renderSummary(); });

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */
  restore();
  // Load every step that already has a pick (so the summary can price it),
  // then the current step, then warm the next one.
  var needed = STEPS.filter(function (s, i) { return state.picks[s.key] || i === state.step; });
  renderAll();
  Promise.all(needed.map(loadStep)).then(function () {
    // Backfill size/fit for URL-restored picks.
    STEPS.forEach(function (s) {
      var pk = state.picks[s.key], v = pickVariant(s.key);
      if (pk && v) { pk.size = v.size; pk.fit = v.fit; }
      else if (pk && pk.variantId && data[s.key] && data[s.key].status === 'ready') { delete state.picks[s.key]; }
    });
    renderAll();
    if (state.expanded) openProduct(state.expanded, state.expandedStep);
    if (state.step < STEPS.length) loadStep(STEPS[state.step]).then(function () { renderPanel(); renderSummary(); });
    if (state.step + 1 < STEPS.length) loadStep(STEPS[state.step + 1]);
    push('suit_builder_start', { step: state.step < STEPS.length ? STEPS[state.step].key : 'review' });
  });
})();
